import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash, Check, X, Eye } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { CsvExportButton } from './CsvHelpers';

export function RequestManagement() {
  const { getAuthHeader } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [replyModal, setReplyModal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [selected, setSelected] = useState(new Set());

  const load = async () => { const r = await axios.get(`${API}/admin/requests`, { headers: getAuthHeader() }); setReqs(r.data); setSelected(new Set()); };
  useEffect(() => { load(); }, []);

  const filtered = filter === 'all' ? reqs : reqs.filter(r => r.request_type === filter);

  const handleStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/requests/${id}`, { status, admin_notes: '' }, { headers: getAuthHeader() });
      toast.success('Updated');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers: getAuthHeader() });
      toast.success('Request deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await axios.post(`${API}/admin/requests/bulk-delete`, { ids: Array.from(selected) }, { headers: getAuthHeader() });
      toast.success(`${selected.size} request(s) deleted`);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const toggleAll = () => {
    if (allFilteredSelected) { setSelected(new Set()); } else { setSelected(new Set(filtered.map(r => r.id))); }
  };

  const openChat = async (req) => {
    setReplyModal(req);
    try {
      const r = await axios.get(`${API}/requests/${req.id}/messages`, { headers: getAuthHeader() });
      setMessages(r.data);
    } catch { setMessages([]); }
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    try {
      await axios.post(`${API}/requests/${replyModal.id}/messages`, { message: newMsg }, { headers: getAuthHeader() });
      const r = await axios.get(`${API}/requests/${replyModal.id}/messages`, { headers: getAuthHeader() });
      setMessages(r.data);
      setNewMsg('');
      toast.success('Reply sent');
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="request-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Employee Requests</h1><p className="text-slate-500 text-sm">{reqs.length} total requests</p></div>
        <div className="flex gap-2 items-center">
          {selected.size > 0 && (
            <Button variant="outline" size="sm" onClick={handleBulkDelete} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="bulk-delete-btn">
              <Trash size={14} className="mr-1" />Delete ({selected.size})
            </Button>
          )}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="holiday">Holidays</SelectItem><SelectItem value="expense">Expenses</SelectItem><SelectItem value="query">Queries</SelectItem></SelectContent>
          </Select>
          <CsvExportButton endpoint="/csv/export/expenses" label="Expenses CSV" />
          <CsvExportButton endpoint="/csv/export/queries" label="Queries CSV" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        {filtered.length > 0 && (
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4 rounded border-slate-300 accent-lime-600" data-testid="select-all-requests" />
            <span className="text-xs text-slate-600 font-medium">{allFilteredSelected ? 'Deselect all' : 'Select all'} ({filtered.length})</span>
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {filtered.map(r => (
            <div key={r.id} className={`p-4 flex items-center gap-3 ${selected.has(r.id) ? 'bg-red-50/40' : ''}`} data-testid={`admin-request-${r.id}`}>
              <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="w-4 h-4 rounded border-slate-300 accent-lime-600 flex-shrink-0" data-testid={`select-request-${r.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${r.request_type === 'holiday' ? 'bg-lime-100 text-lime-700' : r.request_type === 'expense' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>{r.request_type}</span>
                  <p className="font-medium text-sm text-slate-900 truncate">{r.submitted_by_name} - {r.title}</p>
                  {r.cancellation_requested && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Cancel Requested</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{r.description?.substring(0, 120)}</p>
                {r.amount != null && <span className="text-xs text-slate-500">{'\u00A3'}{r.amount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {r.request_type === 'query' && <Button variant="outline" size="sm" onClick={() => openChat(r)} data-testid={`reply-${r.id}`}><Eye size={14} className="mr-1" />View / Reply</Button>}
                <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : r.status === 'cancelled' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                {r.status === 'pending' && (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleStatus(r.id, 'approved')} className="h-7 bg-emerald-600 hover:bg-emerald-700"><Check size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleStatus(r.id, 'rejected')} className="h-7 border-red-300 text-red-600"><X size={12} /></Button>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" title="Delete request" data-testid={`delete-request-${r.id}`}><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-slate-500">No requests</div>}
        </div>
      </div>

      {replyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col" data-testid="chat-modal">
            <div className="p-5 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{replyModal.title}</h3>
              <p className="text-sm text-slate-500">From: {replyModal.submitted_by_name}</p>
              <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-3 rounded">{replyModal.description}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {messages.map(m => (
                <div key={m.id} className={`p-3 rounded-lg text-sm ${m.sender_id === replyModal.submitted_by ? 'bg-slate-100 mr-8' : 'bg-lime-50 ml-8'}`}>
                  <p className="font-medium text-xs text-slate-500 mb-1">{m.sender_name} - {new Date(m.created_at).toLocaleString('en-GB')}</p>
                  <p className="text-slate-700">{m.message}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="text-center text-slate-400 text-sm py-4">No replies yet</p>}
            </div>
            <div className="p-4 border-t border-slate-200 flex gap-2">
              <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a reply..." className="flex-1" onKeyDown={e => e.key === 'Enter' && sendMessage()} data-testid="chat-input" />
              <Button onClick={sendMessage} className="bg-lime-600 hover:bg-lime-700" data-testid="send-reply">Send</Button>
            </div>
            <div className="p-3 border-t border-slate-100 flex justify-end"><Button variant="outline" size="sm" onClick={() => { setReplyModal(null); setMessages([]); }}>Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}
